import { useEffect, useMemo, useRef, useState } from 'react'
import { MemberCard, MemberCardSkeleton } from '../components/MemberCard'
import { RoleFilter } from '../components/RoleFilter'
import { useCompanyMembers } from '../hooks/useCompanyMembers'

export function CompanyPage() {
  const [roleFilter, setRoleFilter] = useState('all')
  const { filtered, availableRoles, positionGroupMap, loading, loadingMore, hasMore, loadMore, error } = useCompanyMembers(roleFilter)
  const sentinelRef = useRef<HTMLDivElement>(null)

  const groupedView = useMemo(() => {
    if (roleFilter !== 'all') return null
    const groupOrder: string[] = []
    const seenGroups = new Set<string>()
    for (const role of availableRoles) {
      const group = positionGroupMap[role]
      if (group && !seenGroups.has(group)) { groupOrder.push(group); seenGroups.add(group) }
    }
    const buckets: Record<string, typeof filtered> = {}
    const ungroupedMembers: typeof filtered = []
    for (const member of filtered) {
      const memberGroups = new Set(member.roles.map((r) => positionGroupMap[r]).filter(Boolean) as string[])
      if (memberGroups.size === 0) {
        ungroupedMembers.push(member)
      } else {
        for (const g of memberGroups) {
          if (!buckets[g]) buckets[g] = []
          buckets[g].push(member)
        }
      }
    }
    const sections = groupOrder
      .filter((g) => buckets[g]?.length)
      .map((g) => ({ groupName: g, members: buckets[g] ?? [] }))
    return { sections, ungroupedMembers }
  }, [roleFilter, filtered, availableRoles, positionGroupMap])

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(
      (entries) => { if (entries[0]?.isIntersecting) loadMore() },
      { rootMargin: '800px' }
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [loadMore])

  return (
    <div className="container mx-auto px-4 py-16">
      <div className="mb-10 max-w-2xl">
        <h1 className="mb-3 text-4xl font-bold tracking-tight">The Company</h1>
        <p className="text-muted-foreground leading-relaxed">
          Meet the artists, choreographers, and creators behind One Day Dance.
        </p>
      </div>

      <div className="mb-8">
        <RoleFilter roles={availableRoles} active={roleFilter} onChange={setRoleFilter} />
      </div>

      {loading && (
        <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
          {Array.from({ length: 10 }).map((_, i) => (
            <MemberCardSkeleton key={i} />
          ))}
        </div>
      )}

      {error && <p className="text-destructive text-sm">{error}</p>}

      {!loading && !error && filtered.length === 0 && (
        <p className="text-muted-foreground text-sm">No members found for this filter.</p>
      )}

      {!loading && !error && filtered.length > 0 && (
        roleFilter !== 'all' || !groupedView || groupedView.sections.length === 0 ? (
          <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
            {filtered.map((member) => (
              <MemberCard key={member.id} member={member} />
            ))}
          </div>
        ) : (
          <div className="space-y-12">
            {groupedView.sections.map((section) => (
              <section key={section.groupName}>
                <h2 className="mb-6 text-2xl font-semibold tracking-tight">{section.groupName}</h2>
                <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                  {section.members.map((member) => (
                    <MemberCard key={member.id} member={member} />
                  ))}
                </div>
              </section>
            ))}
            {groupedView.ungroupedMembers.length > 0 && (
              <section key="__ungrouped__">
                <h2 className="mb-6 text-2xl font-semibold tracking-tight">Other Members</h2>
                <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                  {groupedView.ungroupedMembers.map((member) => (
                    <MemberCard key={member.id} member={member} />
                  ))}
                </div>
              </section>
            )}
          </div>
        )
      )}

      {hasMore && <div ref={sentinelRef} className="h-1" />}

      {loadingMore && (
        <div className="mt-8 grid grid-cols-2 gap-6 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
          {Array.from({ length: 4 }).map((_, i) => (
            <MemberCardSkeleton key={i} />
          ))}
        </div>
      )}
    </div>
  )
}
